import React, { useEffect, useState } from "react";
import {
  MessageSquare,
  TrendingUp,
  Brain,
  Trophy,
  AlertCircle,
  ArrowUpRight,
} from "lucide-react";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";

import { useNavigate } from "react-router-dom";
import { useGlobalContext } from "../AUTH/GlobalContext";

/* ================= MODAL ================= */

const AIInterviewModal = ({ onClose, onStart }) => {
  const [form, setForm] = useState({
    role: "",
    experience: "",
    type: "mixed",
    stack: "",
  });

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl w-full max-w-md p-7 shadow-2xl relative">

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"
        >
          ✕
        </button>

        <h2 className="text-2xl font-bold mb-6 text-gray-900">
          Start AI Interview
        </h2>

        <div className="space-y-4">

          <input
            name="role"
            placeholder="Role (Backend Engineer)"
            className="w-full border border-gray-200 rounded-2xl p-4 outline-none focus:border-purple-500"
            onChange={handleChange}
          />

          <select
            name="experience"
            className="w-full border border-gray-200 rounded-2xl p-4 outline-none focus:border-purple-500"
            onChange={handleChange}
          >
            <option value="">Experience Level</option>
            <option value="junior">Junior</option>
            <option value="mid">Mid</option>
            <option value="senior">Senior</option>
          </select>

          <select
            name="type"
            className="w-full border border-gray-200 rounded-2xl p-4 outline-none focus:border-purple-500"
            onChange={handleChange}
          >
            <option value="mixed">Mixed</option>
            <option value="dsa">DSA</option>
            <option value="system">System Design</option>
            <option value="hr">HR</option>
          </select>

          <input
            name="stack"
            placeholder="Tech Stack (React, Node, SQL)"
            className="w-full border border-gray-200 rounded-2xl p-4 outline-none focus:border-purple-500"
            onChange={handleChange}
          />

        </div>

        <button
          onClick={() => onStart(form)}
          className="w-full mt-6 bg-purple-600 hover:bg-purple-700 text-white py-4 rounded-2xl font-semibold transition"
        >
          Start Interview
        </button>
      </div>
    </div>
  );
};

/* ================= MAIN COMPONENT ================= */

const FinalInterview = () => {
  const { user } = useGlobalContext();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [interviewHistory, setInterviewHistory] = useState([]);

  /* ================= FETCH ================= */

  useEffect(() => {
    if (!user) return;

    fetch(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/interviews/history?userId=${user._id}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setInterviewHistory(data.interviews || []);
        }
      })
      .catch((err) => console.error(err));
  }, [user]);

  /* ================= START INTERVIEW ================= */

  const startAIInterview = (formData) => {
    setShowModal(false);
    navigate("/ai-interview", { state: formData });
  };

  /* ================= MOCK DATA ================= */

  const metrics = [
    {
      title: "Overall Score",
      value: "78/100",
      change: "+12%",
      icon: Trophy,
      color: "bg-purple-100 text-purple-600",
    },
    {
      title: "Interviews Completed",
      value: interviewHistory.length,
      change: "+3",
      icon: MessageSquare,
      color: "bg-blue-100 text-blue-600",
    },
    {
      title: "Confidence Score",
      value: "82%",
      change: "+8%",
      icon: TrendingUp,
      color: "bg-green-100 text-green-600",
    },
    {
      title: "Skills Improved",
      value: "6",
      change: "+2",
      icon: Brain,
      color: "bg-orange-100 text-orange-600",
    },
  ];

  const progressData = [
    { date: "May 16", score: 42 },
    { date: "May 17", score: 49 },
    { date: "May 18", score: 58 },
    { date: "May 19", score: 67 },
    { date: "May 20", score: 71 },
    { date: "May 21", score: 75 },
    { date: "May 22", score: 78 },
  ];

  const skillData = [
    { skill: "Communication", score: 82 },
    { skill: "Backend", score: 80 },
    { skill: "Problem Solving", score: 74 },
    { skill: "Confidence", score: 76 },
    { skill: "System Design", score: 68 },
    { skill: "DSA", score: 72 },
  ];

  const weakAreas = [
    "Need stronger DB indexing concepts",
    "Communication pauses detected",
    "System design answers lack scalability depth",
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">

      <main className="max-w-7xl mx-auto px-6 py-8">

        {/* HERO */}

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-10">

          <div>
            <h1 className="text-4xl font-bold text-gray-900">
              Welcome back, {user?.name || "Rohit"} 👋
            </h1>

            <p className="text-gray-500 mt-3 text-lg">
              Track your interview growth and improve with every session.
            </p>
          </div>

          <button
            onClick={() => setShowModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-7 py-4 rounded-2xl font-semibold shadow-lg transition"
          >
            Start New Interview
          </button>
        </div>

        {/* METRIC CARDS */}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10">

          {metrics.map((item, idx) => (
            <div
              key={idx}
              className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm"
            >

              <div className="flex items-start justify-between mb-5">

                <div>
                  <p className="text-gray-500 text-sm mb-2">
                    {item.title}
                  </p>

                  <h2 className="text-3xl font-bold text-gray-900">
                    {item.value}
                  </h2>
                </div>

                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center ${item.color}`}
                >
                  <item.icon className="w-6 h-6" />
                </div>
              </div>

              <div className="flex items-center gap-2 text-green-600 font-medium text-sm">
                <ArrowUpRight className="w-4 h-4" />
                {item.change} from last week
              </div>
            </div>
          ))}
        </div>

        {/* CHARTS */}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-10">

          {/* PROGRESS GRAPH */}

          <div className="xl:col-span-2 bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">

            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Progress Overview
                </h2>

                <p className="text-gray-500 text-sm mt-1">
                  Your interview growth over time
                </p>
              </div>

              <div className="bg-purple-50 text-purple-700 px-4 py-2 rounded-xl text-sm font-medium">
                Overall Score
              </div>
            </div>

            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={progressData}>
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis dataKey="date" />

                <YAxis />

                <Tooltip />

                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#7c3aed"
                  strokeWidth={4}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* RADAR CHART */}

          <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">

            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Skill Breakdown
            </h2>

            <p className="text-gray-500 text-sm mb-6">
              AI evaluated technical and communication performance
            </p>

            <ResponsiveContainer width="100%" height={320}>
              <RadarChart outerRadius={110} data={skillData}>

                <PolarGrid />

                <PolarAngleAxis dataKey="skill" />

                <PolarRadiusAxis />

                <Radar
                  dataKey="score"
                  stroke="#8b5cf6"
                  fill="#8b5cf6"
                  fillOpacity={0.5}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* INSIGHTS + TOP SKILLS */}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-10">

          {/* WEAK AREAS */}

          <div className="xl:col-span-2 bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">

            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-6 h-6 text-red-500" />

              <h2 className="text-xl font-bold text-gray-900">
                AI Improvement Insights
              </h2>
            </div>

            <div className="space-y-4">

              {weakAreas.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-red-50 border border-red-100 rounded-2xl p-5"
                >
                  <p className="text-red-700 font-medium">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* TOP SKILLS */}

          <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">

            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Top Skills
            </h2>

            <div className="space-y-6">

              {skillData.map((skill, idx) => (
                <div key={idx}>

                  <div className="flex items-center justify-between mb-2">

                    <p className="font-medium text-gray-700">
                      {skill.skill}
                    </p>

                    <p className="text-sm font-semibold text-gray-900">
                      {skill.score}/100
                    </p>
                  </div>

                  <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">

                    <div
                      className="h-full bg-purple-600 rounded-full"
                      style={{
                        width: `${skill.score}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* INTERVIEW HISTORY */}

        <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm mb-10">

          <div className="flex items-center justify-between mb-6">

            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Interview History
              </h2>

              <p className="text-gray-500 text-sm mt-1">
                Track all your previous interviews
              </p>
            </div>
          </div>

          {interviewHistory.length === 0 ? (
            <div className="py-14 text-center">
              <p className="text-gray-500 text-lg">
                No interviews completed yet.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">

              <table className="w-full">

                <thead>
                  <tr className="border-b border-gray-100 text-left">

                    <th className="py-4 text-sm text-gray-500 font-medium">
                      Role
                    </th>

                    <th className="py-4 text-sm text-gray-500 font-medium">
                      Date
                    </th>

                    <th className="py-4 text-sm text-gray-500 font-medium">
                      Score
                    </th>

                    <th className="py-4 text-sm text-gray-500 font-medium">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody>

                  {interviewHistory.map((i) => (
                    <tr
                      key={i._id}
                      className="border-b border-gray-50 hover:bg-gray-50 transition"
                    >

                      <td className="py-5">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {i.role || "Software Engineer"}
                          </p>

                          <p className="text-sm text-gray-500">
                            AI Interview
                          </p>
                        </div>
                      </td>

                      <td className="py-5 text-gray-600">
                        {new Date(i.createdAt).toLocaleDateString()}
                      </td>

                      <td className="py-5">
                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-xl text-sm font-semibold">
                          78/100
                        </span>
                      </td>

                      <td className="py-5">
                        <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-xl text-sm font-medium">
                          Completed
                        </span>
                      </td>
                    </tr>
                  ))}

                </tbody>
              </table>
            </div>
          )}
        </div>

      </main>

      {/* MODAL */}

      {showModal && (
        <AIInterviewModal
          onClose={() => setShowModal(false)}
          onStart={startAIInterview}
        />
      )}
    </div>
  );
};

export default FinalInterview;